import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react"

import { useAgentsById } from "@/agents/catalog"
import { useKnowledgeBaseEntriesTransport } from "@/agents/knowledge-base"
import {
  useTestSetMetadataTransport,
  useTestSetRecordsTransport,
} from "@/test-set/transport"
import { useScopedStorageKey } from "@/workspaces"

import {
  SIMULATION_INPUT_VIEW_MODE,
  type ObjectViewMode,
  type SimulationInputViewMode,
} from "@/lib/viewModes"
import { useQueryParams } from "@/hooks/useQueryParams"
import type { TestSetRecord } from "@/types/api/TestSetRecord"

import type { LocalMediaAttachment } from "../components/MediaAttachmentSection"
import type { ToggleableColumn } from "../components/test-case-list/TestCaseColumnVisibility"
import {
  clearLiveTestSetDisplayName,
  setLiveTestSetDisplayName,
} from "../hooks/useLiveTestSetEditorState"
import { useLocalMediaAttachments } from "../hooks/useLocalMediaAttachments"
import {
  useMetadataColumns,
  type MetadataColumn,
} from "../hooks/useMetadataColumns"
import {
  useMetadataColumnState,
  type MetadataColumnStateApi,
} from "../hooks/useMetadataColumnState"
import { useTableState } from "../hooks/useTableState"
import { useTestSetEditorEventHandlers } from "../hooks/useTestSetEditorEventHandlers"
import { useViewModes } from "../hooks/useViewModes"
import {
  type EditableTestCase,
  type HistoryEntry,
  type PendingChanges,
  type TestSetContext,
  type TestSetEditorFeatureFlags,
  type TestSetMetadata,
} from "../model"
import {
  filterTestCases,
  getAvailableTestCaseTypes,
  hasActiveTestCaseFilters,
} from "../utils/filterTestCases"
import {
  appendHistoryEntry,
  buildTestSetContext,
  createHistoryEntry,
  getInitialEditorState,
} from "../utils/testSetEditorContextState"
import { hasTestSetEditorChanges } from "../utils/testSetEditorState"

interface TestSetEditorContextType {
  // Test set metadata
  testSetId: string
  metadata: TestSetMetadata
  isLoadingMetadata: boolean

  // Test cases
  testCases: EditableTestCase[]
  isLoadingTestCases: boolean
  selectedTestCaseId: string | null
  selectedTestCaseIds: Set<string>
  expectedBehaviorTestCaseId: string | null
  requestExpectedBehaviorTest: (testCaseId: string) => void
  closeExpectedBehaviorTest: () => void
  isExpectedBehaviorTestRunning: boolean
  setIsExpectedBehaviorTestRunning: (isRunning: boolean) => void
  pendingExpectedBehaviorTestCaseId: string | null
  confirmExpectedBehaviorTestSwitch: () => void
  cancelExpectedBehaviorTestSwitch: () => void

  // Search & filtering (view-only; does not affect the editable test cases)
  testCaseSearchQuery: string
  setTestCaseSearchQuery: (query: string) => void
  testCaseTypeFilters: string[]
  setTestCaseTypeFilters: (types: string[]) => void
  clearTestCaseFilters: () => void
  hasActiveTestCaseFilters: boolean
  availableTestCaseTypes: string[]
  /** testCases narrowed to the active search/type filters. */
  visibleTestCases: EditableTestCase[]
  /** Every row the table renders: visibleTestCases, then the staged additions below them. Selection spans both, so a staged row can be selected before the test set is saved. */
  selectableTestCases: EditableTestCase[]

  // Test set context (for copilot)
  testSetContext: TestSetContext | null

  // Pending changes
  pendingChanges: PendingChanges | null

  // Change detection
  hasChanges: boolean

  // Pending local media files (selected in editor, uploaded on save)
  localMediaAttachmentsByTestCaseId: Record<string, LocalMediaAttachment>
  getLocalMediaAttachment: (testCaseId: string) => LocalMediaAttachment | null
  setLocalMediaAttachment: (
    testCaseId: string,
    attachment: LocalMediaAttachment | null,
  ) => void
  clearLocalMediaAttachments: () => void

  // Initial state for reset
  initialTestCases: EditableTestCase[]
  initialMetadata: TestSetMetadata

  // History for undo
  history: HistoryEntry[]
  canUndo: boolean

  // Metadata view mode (global)
  metadataViewMode: ObjectViewMode
  setMetadataViewMode: (mode: ObjectViewMode) => void

  // Simulation input view mode
  simulationInputViewMode: SimulationInputViewMode
  setSimulationInputViewMode: (mode: SimulationInputViewMode) => void

  // Agent associations
  selectedAgentIds: string[]
  activeAgentId: string
  setSelectedAgentIds: (ids: string[]) => void
  setActiveAgentId: (id: string) => void

  // Column visibility
  visibleColumns: Set<ToggleableColumn>
  toggleColumn: (key: ToggleableColumn) => void

  // Metadata-as-columns
  metadataColumns: MetadataColumn[]
  metadataColumnState: MetadataColumnStateApi

  // Initialization state
  isInitialized: boolean

  // Route-owned feature gates for editor behavior
  featureFlags: TestSetEditorFeatureFlags

  // Expand/collapse all
  isAllExpanded: boolean
  handleToggleExpandAll: () => void
}

const NO_PENDING_ADDITIONS: EditableTestCase[] = []

const TestSetEditorContext = createContext<
  TestSetEditorContextType | undefined
>(undefined)

interface TestSetEditorProviderProps {
  children: ReactNode
  testSetId: string
  featureFlags: TestSetEditorFeatureFlags
  initialMetadata?: {
    metadata?: Partial<TestSetRecord> & { display_name: string }
  }
}

export const TestSetEditorProvider: React.FC<TestSetEditorProviderProps> = ({
  children,
  testSetId,
  featureFlags,
  initialMetadata: initialMetadataProp,
}) => {
  const queryParams = useQueryParams()

  // Scoped by org + workspace: agent ids handed off from the create page.
  const selectedAgentsStorageKey = useScopedStorageKey(
    "testset_selected_agents",
  )

  // Core test-case state
  const [testCases, setTestCases] = useState<EditableTestCase[]>([])
  const [selectedTestCaseId, setSelectedTestCaseId] = useState<string | null>(
    null,
  )
  const [selectedTestCaseIds, setSelectedTestCaseIds] = useState<Set<string>>(
    new Set(),
  )
  const [expectedBehaviorTestCaseId, setExpectedBehaviorTestCaseId] = useState<
    string | null
  >(null)
  const [isExpectedBehaviorTestRunning, setIsExpectedBehaviorTestRunning] =
    useState(false)
  const [
    pendingExpectedBehaviorTestCaseId,
    setPendingExpectedBehaviorTestCaseId,
  ] = useState<string | null>(null)
  const openExpectedBehaviorTest = useCallback((testCaseId: string) => {
    setSelectedTestCaseId(testCaseId)
    setExpectedBehaviorTestCaseId(testCaseId)
  }, [])
  const requestExpectedBehaviorTest = useCallback(
    (testCaseId: string) => {
      if (
        isExpectedBehaviorTestRunning &&
        expectedBehaviorTestCaseId &&
        expectedBehaviorTestCaseId !== testCaseId
      ) {
        setPendingExpectedBehaviorTestCaseId(testCaseId)
        return
      }
      openExpectedBehaviorTest(testCaseId)
    },
    [
      expectedBehaviorTestCaseId,
      isExpectedBehaviorTestRunning,
      openExpectedBehaviorTest,
    ],
  )
  const confirmExpectedBehaviorTestSwitch = useCallback(() => {
    if (pendingExpectedBehaviorTestCaseId) {
      openExpectedBehaviorTest(pendingExpectedBehaviorTestCaseId)
    }
    setPendingExpectedBehaviorTestCaseId(null)
    setIsExpectedBehaviorTestRunning(false)
  }, [openExpectedBehaviorTest, pendingExpectedBehaviorTestCaseId])
  const cancelExpectedBehaviorTestSwitch = useCallback(() => {
    setPendingExpectedBehaviorTestCaseId(null)
  }, [])
  const closeExpectedBehaviorTest = useCallback(() => {
    setExpectedBehaviorTestCaseId(null)
    setPendingExpectedBehaviorTestCaseId(null)
    setIsExpectedBehaviorTestRunning(false)
  }, [])
  const lastToggledIndexRef = useRef<number | null>(null)
  const [pendingChanges, setPendingChanges] = useState<PendingChanges | null>(
    null,
  )

  const [testCaseSearchQuery, setTestCaseSearchQuery] = useState("")
  const [testCaseTypeFilters, setTestCaseTypeFilters] = useState<string[]>([])
  const clearTestCaseFilters = useCallback(() => {
    setTestCaseSearchQuery("")
    setTestCaseTypeFilters([])
  }, [])
  const [metadata, setMetadata] = useState<TestSetMetadata>({
    displayName: "",
    description: "",
  })

  // Track initial values for change detection
  const [initialTestCases, setInitialTestCases] = useState<EditableTestCase[]>(
    [],
  )
  const [initialMetadata, setInitialMetadata] = useState<TestSetMetadata>({
    displayName: "",
    description: "",
  })
  const [initialSelectedAgentIds, setInitialSelectedAgentIds] = useState<
    string[]
  >([])
  const [initialActiveAgentId, setInitialActiveAgentId] = useState<string>("")
  const [isInitialized, setIsInitialized] = useState(false)

  // History for undo functionality
  const [history, setHistory] = useState<HistoryEntry[]>([])
  const MAX_HISTORY = 20

  // Agent associations
  const [selectedAgentIds, setSelectedAgentIds] = useState<string[]>([])
  const [activeAgentId, setActiveAgentId] = useState<string>("")

  // Focused state hooks
  const {
    metadataViewMode,
    setMetadataViewMode,
    simulationInputViewMode,
    setSimulationInputViewMode,
  } = useViewModes()

  const {
    localMediaAttachmentsByTestCaseId,
    getLocalMediaAttachment,
    setLocalMediaAttachment,
    clearLocalMediaAttachments,
  } = useLocalMediaAttachments(testCases)

  const {
    visibleColumns,
    setVisibleColumns,
    toggleColumn,
    isAllExpanded,
    handleToggleExpandAll,
  } = useTableState()

  const metadataColumnState = useMetadataColumnState(testSetId)
  const metadataColumns = useMetadataColumns(
    testCases,
    metadataColumnState.addedColumns,
  )

  // Fetch test set metadata (only if not provided via props)
  const { data: fetchedMetadata, isLoading: isLoadingMetadata } =
    useTestSetMetadataTransport(testSetId, {
      enabled: !!testSetId && !initialMetadataProp,
    })

  const effectiveMetadata = initialMetadataProp || fetchedMetadata

  const isNewUnsaved = !testSetId
  const isManualMode = queryParams.get("manual") === "true"
  const { data: testCaseRecords, isLoading: isLoadingTestCases } =
    useTestSetRecordsTransport(
      { test_set_id: testSetId },
      { enabled: !!testSetId },
    )

  const effectiveTestCaseRecords = useMemo(() => {
    if (!isNewUnsaved) {
      return testCaseRecords
    }
    if (isManualMode) {
      return [
        {
          id: `new-${Date.now()}`,
          input_str: "",
          description: "",
          expected_behaviors: [],
          expected_output_json: null,
          s3_key: null,
          s3_bucket: null,
          input_type: "SCENARIO",
          script_turns: null,
          simulation_metadata_input: null,
          connected_knowledge_base_entries: [],
          media_attachments: [],
        },
      ]
    }
    return []
  }, [isNewUnsaved, isManualMode, testCaseRecords])

  const { agentsById, isLoading: isLoadingAgents } = useAgentsById()

  // Initialize test cases and metadata from fetched data.
  // Wait for agents to load before initializing to ensure agent associations are set correctly.
  useEffect(() => {
    if (
      effectiveMetadata?.metadata &&
      (effectiveTestCaseRecords || isNewUnsaved) &&
      !isInitialized &&
      (isNewUnsaved || !isLoadingAgents)
    ) {
      let storedSelectedAgentIds: string[] | undefined
      if (isNewUnsaved && selectedAgentsStorageKey) {
        try {
          const stored = sessionStorage.getItem(selectedAgentsStorageKey)
          if (stored) {
            sessionStorage.removeItem(selectedAgentsStorageKey)
            storedSelectedAgentIds = JSON.parse(stored) as string[]
          }
        } catch {
          // Ignore
        }
      }

      const initialState = getInitialEditorState({
        agentsById,
        isNewUnsaved,
        metadata: effectiveMetadata.metadata,
        storedSelectedAgentIds,
        testCaseRecords: effectiveTestCaseRecords || [],
        testSetId,
      })

      setMetadata(initialState.metadata)
      setInitialMetadata(initialState.initialMetadata)
      setSelectedAgentIds(initialState.selectedAgentIds)
      setInitialSelectedAgentIds(initialState.initialSelectedAgentIds)
      setActiveAgentId(initialState.activeAgentId)
      setInitialActiveAgentId(initialState.initialActiveAgentId)
      setTestCases(initialState.testCases)
      setInitialTestCases(initialState.initialTestCases)
      setIsInitialized(true)

      if (initialState.columnsToShow.length > 0) {
        setVisibleColumns((prev) => {
          const next = new Set(prev)
          for (const column of initialState.columnsToShow) {
            next.add(column)
          }
          if (next.size === prev.size) {
            return prev
          }
          return next
        })
      }

      if (isManualMode && initialState.testCases.length > 0) {
        setTimeout(() => {
          const cell = document.querySelector<HTMLElement>(
            `[data-test-case-id="${initialState.testCases[0].id}"] td[aria-label="Edit test case"]`,
          )
          if (cell) {
            cell.focus()
            setTimeout(
              () =>
                cell.dispatchEvent(
                  new KeyboardEvent("keydown", { key: "Enter", bubbles: true }),
                ),
              50,
            )
          }
        }, 200)
      }

      if (
        initialState.simulationInputViewMode ===
        SIMULATION_INPUT_VIEW_MODE.MARKDOWN
      ) {
        setSimulationInputViewMode(SIMULATION_INPUT_VIEW_MODE.MARKDOWN)
      }
    }
  }, [
    effectiveMetadata,
    effectiveTestCaseRecords,
    isNewUnsaved,
    isManualMode,
    agentsById,
    testSetId,
    isInitialized,
    isLoadingAgents,
    selectedAgentsStorageKey,
    setVisibleColumns,
    setSimulationInputViewMode,
  ])

  // Helper to save current state to history
  const saveToHistory = useCallback(
    (actionType: HistoryEntry["actionType"], description: string) => {
      const entry = createHistoryEntry(
        actionType,
        description,
        testCases,
        selectedTestCaseId,
      )

      setHistory((prev) => appendHistoryEntry(prev, entry, MAX_HISTORY))
    },
    [testCases, selectedTestCaseId, MAX_HISTORY],
  )

  const getLastToggledIndex = useCallback(() => {
    return lastToggledIndexRef.current
  }, [])

  const setLastToggledIndex = useCallback((index: number | null) => {
    lastToggledIndexRef.current = index
  }, [])

  // Calculate if there are changes (exclude displayName so renaming does not trigger Esc/Save)
  const hasChanges = useMemo(() => {
    return hasTestSetEditorChanges({
      activeAgentId,
      initialActiveAgentId,
      initialMetadata,
      initialSelectedAgentIds,
      initialTestCases,
      localMediaAttachmentCount: Object.keys(localMediaAttachmentsByTestCaseId)
        .length,
      metadata,
      selectedAgentIds,
      testCases,
    })
  }, [
    testCases,
    metadata,
    initialTestCases,
    initialMetadata,
    selectedAgentIds,
    initialSelectedAgentIds,
    activeAgentId,
    initialActiveAgentId,
    localMediaAttachmentsByTestCaseId,
  ])

  const availableTestCaseTypes = useMemo(
    () => getAvailableTestCaseTypes(testCases),
    [testCases],
  )
  const testCaseFilters = useMemo(
    () => ({
      searchQuery: testCaseSearchQuery,
      typeFilters: testCaseTypeFilters,
    }),
    [testCaseSearchQuery, testCaseTypeFilters],
  )
  const filtersActive = hasActiveTestCaseFilters(testCaseFilters)
  const visibleTestCases = useMemo(
    () => filterTestCases(testCases, testCaseFilters),
    [testCases, testCaseFilters],
  )
  const pendingTestCaseAdditions =
    pendingChanges?.type === "ADD" ? pendingChanges.data : NO_PENDING_ADDITIONS
  const selectableTestCases = useMemo(
    () => [...visibleTestCases, ...pendingTestCaseAdditions],
    [visibleTestCases, pendingTestCaseAdditions],
  )

  useTestSetEditorEventHandlers({
    activeAgentId,
    clearLocalMediaAttachments,
    getLastToggledIndex,
    history,
    initialActiveAgentId,
    initialMetadata,
    initialSelectedAgentIds,
    initialTestCases,
    isInitialized,
    metadata,
    pendingChanges,
    saveToHistory,
    selectableTestCases,
    selectedAgentIds,
    selectedTestCaseId,
    selectedTestCaseIds,
    setLastToggledIndex,
    setActiveAgentId,
    setHistory,
    setInitialActiveAgentId,
    setInitialMetadata,
    setInitialSelectedAgentIds,
    setInitialTestCases,
    setIsInitialized,
    setMetadata,
    setPendingChanges,
    setSelectedAgentIds,
    setSelectedTestCaseId,
    setSelectedTestCaseIds,
    setTestCases,
    setVisibleColumns,
    testCases,
  })

  // Build test set context for copilot
  const activeAgent = activeAgentId ? agentsById[activeAgentId] : undefined
  const { data: knowledgeBaseEntries } = useKnowledgeBaseEntriesTransport(
    activeAgentId,
    { enabled: !!activeAgentId },
  )
  const agentContext = useMemo(() => {
    if (!activeAgent) {
      return undefined
    }
    return {
      display_name: activeAgent.display_name,
      model_type: activeAgent.model_type,
      prompt: activeAgent.prompt,
      workflows: activeAgent.workflows,
      knowledge_base: knowledgeBaseEntries?.map((kb) => ({
        name: kb.name,
        description: kb.description,
      })),
    }
  }, [activeAgent, knowledgeBaseEntries])
  const testSetContext: TestSetContext | null = useMemo(() => {
    return buildTestSetContext(metadata.description, agentContext)
  }, [metadata.description, agentContext])

  const canUndo = history.length > 0

  useEffect(() => {
    setLiveTestSetDisplayName(testSetId, metadata.displayName)
    return () => clearLiveTestSetDisplayName(testSetId)
  }, [testSetId, metadata.displayName])

  const value = useMemo(
    () => ({
      testSetId,
      metadata,
      isLoadingMetadata: isLoadingMetadata && !isInitialized,
      testCases,
      isLoadingTestCases: isLoadingTestCases && !isInitialized,
      selectedTestCaseId,
      selectedTestCaseIds,
      expectedBehaviorTestCaseId,
      requestExpectedBehaviorTest,
      closeExpectedBehaviorTest,
      isExpectedBehaviorTestRunning,
      setIsExpectedBehaviorTestRunning,
      pendingExpectedBehaviorTestCaseId,
      confirmExpectedBehaviorTestSwitch,
      cancelExpectedBehaviorTestSwitch,
      testCaseSearchQuery,
      setTestCaseSearchQuery,
      testCaseTypeFilters,
      setTestCaseTypeFilters,
      clearTestCaseFilters,
      hasActiveTestCaseFilters: filtersActive,
      availableTestCaseTypes,
      visibleTestCases,
      selectableTestCases,
      testSetContext,
      pendingChanges,
      hasChanges,
      localMediaAttachmentsByTestCaseId,
      getLocalMediaAttachment,
      setLocalMediaAttachment,
      clearLocalMediaAttachments,
      initialTestCases,
      initialMetadata,
      history,
      canUndo,
      metadataViewMode,
      setMetadataViewMode,
      simulationInputViewMode,
      setSimulationInputViewMode,
      selectedAgentIds,
      activeAgentId,
      setSelectedAgentIds,
      setActiveAgentId,
      visibleColumns,
      toggleColumn,
      metadataColumns,
      metadataColumnState,
      isInitialized,
      featureFlags,
      isAllExpanded,
      handleToggleExpandAll,
    }),
    [
      testSetId,
      metadata,
      isLoadingMetadata,
      isInitialized,
      testCases,
      isLoadingTestCases,
      selectedTestCaseId,
      selectedTestCaseIds,
      expectedBehaviorTestCaseId,
      requestExpectedBehaviorTest,
      closeExpectedBehaviorTest,
      isExpectedBehaviorTestRunning,
      pendingExpectedBehaviorTestCaseId,
      confirmExpectedBehaviorTestSwitch,
      cancelExpectedBehaviorTestSwitch,
      testCaseSearchQuery,
      setTestCaseSearchQuery,
      testCaseTypeFilters,
      setTestCaseTypeFilters,
      clearTestCaseFilters,
      filtersActive,
      availableTestCaseTypes,
      visibleTestCases,
      selectableTestCases,
      testSetContext,
      pendingChanges,
      hasChanges,
      localMediaAttachmentsByTestCaseId,
      getLocalMediaAttachment,
      setLocalMediaAttachment,
      clearLocalMediaAttachments,
      initialTestCases,
      initialMetadata,
      history,
      canUndo,
      metadataViewMode,
      setMetadataViewMode,
      simulationInputViewMode,
      setSimulationInputViewMode,
      selectedAgentIds,
      activeAgentId,
      visibleColumns,
      toggleColumn,
      metadataColumns,
      metadataColumnState,
      isAllExpanded,
      featureFlags,
      handleToggleExpandAll,
    ],
  )

  return (
    <TestSetEditorContext.Provider value={value}>
      {children}
    </TestSetEditorContext.Provider>
  )
}

export const useTestSetEditorContext = () => {
  const context = useContext(TestSetEditorContext)
  if (!context) {
    throw new Error(
      "useTestSetEditorContext must be used within TestSetEditorProvider",
    )
  }
  return context
}
